import {
  faAnglesRight,
  faCreditCard
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type ReactNode } from "react";
import { clsx } from "clsx";

import { T } from "@repo/transifex";
import {
  BottomActionsChoiceSlot,
  BottomActionsCompactSlot,
  SecondaryButton
} from "~/components/kiosk/bottom-actions/bottom-actions";
import { useUserProductsKiosk } from "~/api/products/hooks/use-user-products";
import { SwishIcon } from "~/components/SwishIcon";
import { useKioskStore } from "~/routes/__app.kiosk/route";
import { useKioskProducts } from "~/stores/kiosk-session/hooks/use-kiosk-products";
import { kioskMenuRequiresBasket } from "~/utils/kiosk-requires-order-instructions";

import { getKioskPaymentButtons } from "./get-kiosk-payment-buttons";
import { useKioskCheckoutStart } from "./use-kiosk-checkout-start";
import { useKioskCheckoutStatus } from "./use-kiosk-checkout-status";

type MethodButtonProps = {
  onClick: () => void;
  disabled?: boolean;
};

const CompactMethodButton = ({
  icon,
  label,
  className,
  onClick,
  disabled
}: MethodButtonProps & {
  icon: ReactNode;
  label: ReactNode;
  className: string;
}) => (
  <button
    type="button"
    className={clsx(
      "flex flex-1 items-center justify-center gap-x-4 rounded-2xl",
      className
    )}
    onClick={onClick}
    disabled={disabled}
  >
    {icon}
    <span className="text-3xl font-bold">{label}</span>
  </button>
);

const CompactCardButton = ({ onClick, disabled }: MethodButtonProps) => (
  <CompactMethodButton
    className="bg-kiosk-actions-sheet-primary-background text-kiosk-actions-sheet-primary-text"
    icon={<FontAwesomeIcon icon={faCreditCard} fontSize={32} />}
    label={<T _str="Card" />}
    onClick={onClick}
    disabled={disabled}
  />
);

const CompactSwishButton = ({ onClick, disabled }: MethodButtonProps) => (
  <CompactMethodButton
    className="bg-white text-black"
    icon={<SwishIcon size={32} />}
    label="Swish"
    onClick={onClick}
    disabled={disabled}
  />
);

export const MenuQuickCheckoutButtons = () => {
  const till = useKioskStore((state) => state.till);
  const isUserIdentified = useKioskStore((state) => state.user !== undefined);
  const { data: userProducts } = useUserProductsKiosk();
  const { unitTotalPayableInclVat } = useKioskProducts();
  const startPayment = useKioskCheckoutStart();

  const { status: checkoutStatus } = useKioskCheckoutStatus();
  const isStartingPayment = checkoutStatus === "pending";

  if (kioskMenuRequiresBasket(till, userProducts?.user?.groupName)) {
    return null;
  }

  const buttons = getKioskPaymentButtons({
    till,
    unitTotalPayableInclVat,
    isUserIdentified
  });

  if (buttons.type === "none") {
    return null;
  }

  if (buttons.type === "choice") {
    return (
      <BottomActionsChoiceSlot>
        {buttons.methods.includes("card") ? (
          <CompactCardButton
            disabled={isStartingPayment}
            onClick={() => {
              startPayment("card");
            }}
          />
        ) : null}
        {buttons.methods.includes("swish") ? (
          <CompactSwishButton
            disabled={isStartingPayment}
            onClick={() => {
              startPayment("swish");
            }}
          />
        ) : null}
      </BottomActionsChoiceSlot>
    );
  }

  return (
    <BottomActionsCompactSlot>
      <SecondaryButton
        className="h-full flex-col gap-y-5"
        icon={faAnglesRight}
        disabled={isStartingPayment}
        onClick={() => {
          startPayment(buttons.method);
        }}
      >
        {buttons.method === "credit" || buttons.method === "billing" ? (
          <T _str="Confirm" />
        ) : (
          <T _str="Pay" />
        )}
      </SecondaryButton>
    </BottomActionsCompactSlot>
  );
};
